// src/pages/OptimizationPage.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { currentOrigin, useBackTarget, withReturnTo } from '../modules/pricingJourney/navigation';
import HistoryIcon from '@mui/icons-material/History';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  AlertTitle,
  Autocomplete,
  Stack,
  Container,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';

// --- ICONOS UPDATE V2.0 (DARK PREMIUM) ---
import SecurityIcon from '@mui/icons-material/Security';      // Estrategia Conservadora
import BalanceIcon from '@mui/icons-material/Balance';        // Estrategia Equilibrada
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'; // Estrategia Agresiva
import PersonIcon from '@mui/icons-material/Person';          // Cliente
import InventoryIcon from '@mui/icons-material/Inventory2';   // Producto
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'; // Icono IA
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'; // Nuevo Item
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import {
  optimizePrice,
  getClientIds,
  getProductIds,
  getProductAttributesFormSchema,
  getClientAttributesFormSchema,
  prepareOptimizationCache,
  getOptimizationCacheStatus,
  type OptimizationInput,
  type OptimizationOutput,
  type AttributeSchema,
  type ProductAttributesFormSchemaAPIResponse,
  type ClientAttributesFormSchemaAPIResponse,
  type OptimizationCacheStatusResponse,
} from '../services/optimizationService';

import { getRetrainingStatus } from '../services/diagnosisService';

import { useAuth } from '../context/AuthContext';
import ValueBridge from '../components/Optimization/ValueBridge'; 
import OptimizationLimitWidget from '../components/Optimization/OptimizationLimitWidget';
import SystemBootLoader from '../components/Common/SystemBootLoader';
import EmptyStateHero from '../components/Common/EmptyStateHero';
import PageHeader from '../modules/pricingJourney/components/PageHeader';

const stealthInputSx = {
    '& .MuiFilledInput-root': {
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid #334155',
        borderRadius: 1,
        fontSize: '1rem', // Tamaño legible pero no gigante
        transition: 'border-color 0.2s',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            borderColor: '#64748b'
        },
        '&.Mui-focused': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            borderColor: '#38bdf8',
            boxShadow: 'none'
        },
        '&:before, &:after': { display: 'none' },
        // CORRECCIÓN CRÍTICA: Forzamos altura y padding para separar etiqueta de valor
        '& .MuiFilledInput-input': {
            paddingTop: '24px !important', // Empuja el valor hacia abajo
            paddingBottom: '8px !important',
            paddingLeft: '12px !important', // Un poco a la derecha como pediste
        }
    },
    '& .MuiInputLabel-root': { 
        color: '#64748b', 
        fontSize: '0.85rem',
        transform: 'translate(12px, 12px) scale(1)', // Posición inicial ajustada
        '&.MuiInputLabel-shrink': {
            transform: 'translate(12px, 6px) scale(0.75)' // Posición flotante ajustada
        }
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#38bdf8' },
    '& .MuiSelect-icon': { color: '#64748b' },
    '& .MuiAutocomplete-inputRoot': {
        paddingTop: '0px !important', 
        paddingBottom: '0px !important'
    }
};

const glassCardSx = {
    bgcolor: '#1e293b', 
    border: '1px solid #334155',
    borderRadius: 2, // Reducido de 3 a 2
    p: 2, // Reducido de 3 a 2 (Más compacto)
    boxShadow: 'none' // ELIMINADA SOMBRA EXTERNA
};

// --- ESTADOS PARA LA MÁQUINA DE ESTADOS DE UI ---
type UIState =
  | { name: 'idle' }
  | { name: 'optimizing' }
  | { name: 'success'; finalData: OptimizationOutput }
  | { name: 'error'; error: Error };

const OptimizationPage: React.FC = () => {
  const { user, refreshProfile } = useAuth(); 
  const navigate = useNavigate();
  // PRC-027: «Volver» al centro de Optimización (o a la guía si viene de ella).
  const back = useBackTarget('/optimization-center', 'Volver a Optimización');
  const backButton = (
    <Button
      size="small"
      startIcon={<ArrowBackRoundedIcon />}
      onClick={back.go}
      sx={{ alignSelf: 'flex-start', minWidth: 0, px: 0, mb: 2, color: '#7dd3fc', textTransform: 'none', fontWeight: 850, '&:hover': { bgcolor: 'transparent', color: '#bae6fd' } }}
    >
      {back.label}
    </Button>
  );
  const [uiState, setUiState] = useState<UIState>({ name: 'idle' });
  const [strategySensitivity, setStrategySensitivity] = useState<number>(50);

  // 1. CONSULTA DE ESTADO DEL MOTOR (OMEGA CORE)
  const { data: retrainingStatus, isLoading: isStatusLoading } = useQuery({
    queryKey: ['retraining-status'],
    queryFn: getRetrainingStatus,
    refetchInterval: (query) => query.state.data?.status === 'running' ? 5000 : 60000
  });

  // 2. DERIVACIÓN DEL ESTADO DEL SISTEMA
  const systemState = useMemo(() => {
    if (isStatusLoading) return 'LOADING';
    if (!retrainingStatus || retrainingStatus.status === 'not_found') return 'NO_DATA';
    if (retrainingStatus.status === 'ready_to_train') return 'NEEDS_TRAINING';
    // Optimización individual SÍ puede funcionar en AUDIT_PENDING porque el modelo ya está entrenado
    return 'ACTIVE';
  }, [retrainingStatus, isStatusLoading]);

  // --- READINESS DEL SERVING DE PRICING ---
  const cachePreparationRequestedForUserRef = useRef<string | null>(null);
  const [cachePreparationTriggerError, setCachePreparationTriggerError] =
    useState<string | null>(null);
  const [cacheReadinessTimedOut, setCacheReadinessTimedOut] =
    useState(false);

  const {
    data: optimizationCacheStatus,
    isError: isOptimizationCacheStatusError,
    error: optimizationCacheStatusError,
    refetch: refetchOptimizationCacheStatus,
  } = useQuery<OptimizationCacheStatusResponse, Error>({
    queryKey: ['optimization-cache-status', user?.id],
    queryFn: getOptimizationCacheStatus,
    enabled: Boolean(user?.id) && systemState === 'ACTIVE',
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      if (cacheReadinessTimedOut) {
        return false;
      }

      const currentStatus = query.state.data?.status;

      if (
        currentStatus === 'ready' ||
        currentStatus === 'failed'
      ) {
        return false;
      }

      return 3000;
    },
  });

  const isOptimizationCacheReady =
    optimizationCacheStatus?.status === 'ready';

  const optimizationCacheFailureMessage =
    cachePreparationTriggerError ||
    optimizationCacheStatus?.message ||
    optimizationCacheStatusError?.message ||
    null;

  const isOptimizationCacheFailed =
    optimizationCacheStatus?.status === 'failed' ||
    isOptimizationCacheStatusError ||
    cachePreparationTriggerError !== null;

  const isOptimizationCachePreparing =
    systemState === 'ACTIVE' &&
    !isOptimizationCacheReady &&
    !isOptimizationCacheFailed &&
    !cacheReadinessTimedOut;

  // Reiniciamos exclusivamente el estado local cuando cambia el usuario.
  useEffect(() => {
    cachePreparationRequestedForUserRef.current = null;
    setCachePreparationTriggerError(null);
    setCacheReadinessTimedOut(false);
  }, [user?.id]);

  // Solicita la preparación únicamente si Redis indica que no existe
  // o que una preparación anterior terminó explícitamente en fallo.
  useEffect(() => {
    if (!user?.id || systemState !== 'ACTIVE') {
      return;
    }

    const currentStatus = optimizationCacheStatus?.status;

    if (
      currentStatus !== 'not_found' &&
      currentStatus !== 'failed'
    ) {
      return;
    }

    if (
      cachePreparationRequestedForUserRef.current === user.id
    ) {
      return;
    }

    cachePreparationRequestedForUserRef.current = user.id;
    setCachePreparationTriggerError(null);

    let cancelled = false;

    const requestCachePreparation = async () => {
      const preparationResponse =
        await prepareOptimizationCache();

      if (cancelled) {
        return;
      }

      if (
        preparationResponse.status ===
        'cache_preparation_failed'
      ) {
        setCachePreparationTriggerError(
          'No se pudo iniciar la preparación del motor de precios.',
        );
        return;
      }

      // Actualiza inmediatamente el estado después del POST.
      await refetchOptimizationCacheStatus();
    };

    void requestCachePreparation();

    return () => {
      cancelled = true;
    };
  }, [
    user?.id,
    systemState,
    optimizationCacheStatus?.status,
    refetchOptimizationCacheStatus,
  ]);

  // El sondeo es acotado. Nunca dejamos la pantalla consultando
  // indefinidamente un worker que podría haber desaparecido.
  useEffect(() => {
    if (
      systemState !== 'ACTIVE' ||
      isOptimizationCacheReady ||
      isOptimizationCacheFailed
    ) {
      setCacheReadinessTimedOut(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCacheReadinessTimedOut(true);
    }, 180000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    user?.id,
    systemState,
    isOptimizationCacheReady,
    isOptimizationCacheFailed,
  ]);

  // Estados para selección principal
  const [clienteId, setClienteId] = useState<string | null>(null);
  const [productoId, setProductoId] = useState<string | null>(null);
  const [costeActual, setCosteActual] = useState('');
  const [cantidadInput, setCantidadInput] = useState<string>('');

  // Estados para controlar UI de "Nuevo Cliente" y "Nuevo Producto"
  const [isNewClientUI, setIsNewClientUI] = useState<boolean>(false);
  const [isNewProductUI, setIsNewProductUI] = useState<boolean>(false);

  // Estados para atributos dinámicos
  const [dynamicClientAttrs, setDynamicClientAttrs] = useState<Record<string, string | null>>({});
  const [dynamicProductAttrs, setDynamicProductAttrs] = useState<Record<string, string | null>>({});

  const [selectedStrategy, setSelectedStrategy] = useState<string>('equilibrado');

  // --- React Query para cargar opciones de Clientes Existentes ---
  const {
    data: clientOptions = [],
    isLoading: loadingClients,
    isError: isClientError,
    error: clientError
  } = useQuery<string[], Error>({
    queryKey: ['clientIds'],
    queryFn: getClientIds,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // --- React Query para cargar opciones de Productos Existentes ---
  const {
    data: productOptions = [],
    isLoading: loadingProducts,
    isError: isProductError,
    error: productError
  } = useQuery<string[], Error>({
    queryKey: ['productIds'],
    queryFn: getProductIds,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // --- React Query para cargar el esquema de atributos de NUEVO CLIENTE ---
  const {
    data: clientAttrSchemaResponse,
    isLoading: loadingClientAttrSchema,
    // isError: isClientAttrSchemaError,
    error: clientAttrSchemaError,
  } = useQuery<ClientAttributesFormSchemaAPIResponse, Error>({
    queryKey: ['clientAttributesFormSchema'],
    queryFn: getClientAttributesFormSchema,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: isNewClientUI, // Habilitar solo si isNewClientUI es true
    refetchOnWindowFocus: false,
  });

  const currentClientAttrFeatures: AttributeSchema[] =
    clientAttrSchemaResponse?.success && clientAttrSchemaResponse.data?.new_client_features
      ? clientAttrSchemaResponse.data.new_client_features
      : [];

  // --- React Query para cargar el esquema de atributos de NUEVO PRODUCTO ---
  const {
    data: productAttrSchemaResponse,
    isLoading: loadingProductAttrSchema,
    // isError: isProductAttrSchemaError,
    error: productAttrSchemaError,
  } = useQuery<ProductAttributesFormSchemaAPIResponse, Error>({
    queryKey: ['productAttributesFormSchema'],
    queryFn: getProductAttributesFormSchema,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: isNewProductUI, // Habilitar solo si isNewProductUI es true
    refetchOnWindowFocus: false,
  });

  const currentProductAttrFeatures: AttributeSchema[] =
    productAttrSchemaResponse?.success && productAttrSchemaResponse.data?.new_product_features
      ? productAttrSchemaResponse.data.new_product_features
      : [];

  // --- Estados derivados para UI de carga/error de opciones ---
  const loadingInitialOptions = loadingClients || loadingProducts;
  const initialOptionsError = isClientError || isProductError ? "Error al cargar listas de clientes o productos." : null;

  const optimizationMutation = useMutation<OptimizationOutput, Error, { payload: OptimizationInput }>({
  mutationFn: (vars) => optimizePrice(vars.payload, false),
  onSuccess: (data) => {
    setUiState({ name: 'success', finalData: data });

    // --- NUEVO: ACTUALIZAR CONTADOR EN TIEMPO REAL ---
    // Esperamos 1s para asegurar que el Backend terminó de sumar +1
    setTimeout(() => {
        refreshProfile().catch(console.error);
    }, 1000);
  },
  onError: (error) => {
    setUiState({ name: 'error', error });
  }
});

  // Control de Límite de Tiers
  const [isLimitReached, setIsLimitReached] = useState(false);

  const RequiredIndicator = () => (
    <Box component="span" sx={{ color: 'error.main', ml: 0.5, fontWeight: 'bold' }}>*</Box>
    // O algo más visual:
    // <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: 'error.main', ml: 0.5, verticalAlign: 'super', fontSize: '0.6em' }}> </Box>
  );

  const getFieldLabel = (baseLabel: string, isRequired: boolean) => (
    <>
      {baseLabel}
      {isRequired && <RequiredIndicator />}
    </>
);

  // --- useEffects para limpiar campos cuando se cambia el estado de "Nuevo" ---
  useEffect(() => {
    if (isNewClientUI) {
      setClienteId(null); // Limpiar cliente existente seleccionado
      setDynamicClientAttrs({}); // Limpiar atributos dinámicos de cliente
    } else {
      setDynamicClientAttrs({});
    }
  }, [isNewClientUI]);

  useEffect(() => {
    if (isNewProductUI) {
      setProductoId(null); // Limpiar producto existente seleccionado
      setDynamicProductAttrs({}); // Limpiar atributos dinámicos de producto
    } else {
      setDynamicProductAttrs({});
      // Considerar si el coste también debe limpiarse si no es producto nuevo
      // setCosteActual(''); // Opcional: si el coste solo tiene sentido para producto nuevo
    }
  }, [isNewProductUI]);

  // --- Handlers para cambios en los campos dinámicos ---
  const handleDynamicClientAttrChange = (name: string, value: string | null) => {
    setDynamicClientAttrs(prev => ({ ...prev, [name]: value }));
  };

  const handleDynamicProductAttrChange = (name: string, value: string | null) => {
    setDynamicProductAttrs(prev => ({ ...prev, [name]: value }));
  };

  const handleStrategyChange = (
    _event: React.MouseEvent<HTMLElement>,
    newStrategy: string | null,
  ) => {
    if (newStrategy !== null) {
      setSelectedStrategy(newStrategy);
      // Mapeo Preset -> Slider Math
      if (newStrategy === 'conservador') setStrategySensitivity(25);
      if (newStrategy === 'equilibrado') setStrategySensitivity(50);
      if (newStrategy === 'agresivo') setStrategySensitivity(90);
    }
  };

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    setStrategySensitivity(newValue as number);
    setSelectedStrategy(''); // Deselecciona botones para indicar "Modo Manual"
  };

  const [formAttempted, setFormAttempted] = useState<boolean>(false);



  const handleOptimize = (event: React.FormEvent) => {
    event.preventDefault();

    if (!isOptimizationCacheReady) {
      alert(
        'El motor de precios todavía se está preparando. La optimización estará disponible cuando finalice.',
      );
      return;
    }

    setFormAttempted(true);
    setUiState({ name: 'optimizing' });

    if (!user?.id) {
      alert("Error de autenticación. Por favor, inicie sesión de nuevo.");
      return;
    }

    // Validaciones
    if (!isNewClientUI && !clienteId) {
      alert("Debe seleccionar un ID de Cliente existente o marcar 'Nuevo Cliente'.");
      return;
    }
    if (isNewClientUI && currentClientAttrFeatures.some(attr => attr.required && !dynamicClientAttrs[attr.name])) {
      // Esta validación es básica. Idealmente, se especificaría mejor qué atributos de cliente nuevo son obligatorios.
      alert("Debe completar todos los campos requeridos para el nuevo cliente.");
      return;
    }
    if (!isNewProductUI && !productoId) {
      alert("Debe seleccionar un ID de Producto existente o marcar 'Nuevo Producto'.");
      return;
    }
    if (isNewProductUI) {
      const costNum = parseFloat(costeActual);
      if (isNaN(costNum) || costNum <= 0) {
        alert("Para un producto nuevo, el 'Coste del Producto' es obligatorio y debe ser un número positivo.");
        return;
      }
      if (currentProductAttrFeatures.some(attr => attr.required && !dynamicProductAttrs[attr.name])) {
        // Esta validación es básica. Idealmente, se especificaría mejor qué atributos de producto nuevo son obligatorios.
        alert("Debe completar todos los campos requeridos para el nuevo producto.");
        return;
      }
    }


    // --- CONSTRUCCIÓN DE PAYLOAD ROBUSTA Y SEGURA ---
    const newProductAttributesPayload: OptimizationInput['new_product_attributes'] = isNewProductUI
      ? {
          standard_cost: parseFloat(costeActual), // Ya validado
          ...currentProductAttrFeatures.reduce((acc, attr) => {
            const value = dynamicProductAttrs[attr.name];
            if (value !== undefined && value !== null && value !== '') {
              acc[attr.name] = value;
            }
            return acc;
          }, {} as Record<string, any>)
        }
      : null;

    const parsedQty = cantidadInput && !isNaN(parseFloat(cantidadInput)) && parseFloat(cantidadInput) > 0 
        ? parseFloat(cantidadInput) 
        : null;

    const payload: OptimizationInput = {
      client_id: isNewClientUI ? "NEW_CLIENT_PLACEHOLDER" : clienteId!, is_new_client: isNewClientUI, new_client_attributes: isNewClientUI ? dynamicClientAttrs : null,
      product_id: isNewProductUI ? null : productoId, is_new_product: isNewProductUI, new_product_attributes: newProductAttributesPayload,
      strategy_profile: selectedStrategy || 'manual', 
      strategy_sensitivity: strategySensitivity,      
      coste_actual_existente: !isNewProductUI && costeActual ? parseFloat(costeActual) : null,
      generate_pricing_matrix: true, 
      quantity: parsedQty // Enviar null si está vacío
    };
    optimizationMutation.mutate({ payload }); // Invocación corregida
  };

// --- OBTENER DATOS FINALES PARA RENDERIZAR ---
  const getFinalData = (): OptimizationOutput | null => {
    if (uiState.name === 'success') return uiState.finalData;
    return null;
  };
  const finalData = getFinalData();

  const getOptimizeErrorMessage = (error: Error | null): string | null => {
    if (!error) return null;
    let errorMessage = 'Error desconocido al realizar la optimización.';
    const axiosError = error as any;
    if (axiosError.response) {
        const status = axiosError.response.status;
        const detail = axiosError.response.data?.detail;
        if (status === 401) return "Error de autenticación. Por favor, vuelva a iniciar sesión.";
        if (status === 404 && detail?.includes("Cliente") && detail?.includes("no encontrado")) return "Cliente no encontrado con los datos base.";
        if (status === 404 && detail?.includes("Producto") && detail?.includes("no encontrado")) return "Producto no encontrado con los datos base.";
        if (status === 404) return "Recurso no encontrado. Verifique los IDs o atributos proporcionados.";
        if (status === 422 && detail) {
            if (typeof detail === 'string') { return detail; }
            if (Array.isArray(detail)) { return detail.map((e: any) => `${e.loc?.slice(-1)[0] || 'campo'}: ${e.msg}`).join('; '); }
        }
        if(typeof detail === 'string' && detail) return detail;
        errorMessage = `Error del servidor (${status})`;
    } else if (axiosError.request) {
        errorMessage = 'No se pudo conectar con el servidor. Verifique su conexión.';
    } else if (error.message) {
        errorMessage = error.message;
    }
    console.error("Error formateado para UI:", errorMessage, "Error original:", error);
    return errorMessage;
  };


  // --- Lógica para deshabilitar el botón Optimizar ---
  const isCostInvalidForNewProduct = isNewProductUI && (costeActual === '' || isNaN(parseFloat(costeActual)) || parseFloat(costeActual) <= 0);
  const isOptimizeButtonDisabled =
  !isOptimizationCacheReady ||
  loadingInitialOptions ||
  (isNewClientUI && loadingClientAttrSchema) ||
  (isNewProductUI && loadingProductAttrSchema) ||
  uiState.name === 'optimizing' || // <-- MODIFICADO
  (!isNewClientUI && !clienteId) ||
  (!isNewProductUI && !productoId) ||
  isCostInvalidForNewProduct ||
  isLimitReached;

  // Helper para renderizar campos de atributos dinámicos
  const renderDynamicAttributeFields = (
    attributes: AttributeSchema[],
    values: Record<string, string | null>,
    handler: (name: string, value: string | null) => void,
    isLoadingSchema: boolean,
    schemaError: Error | null,
    schemaErrorResponse?: any, // Para mensajes de error específicos de la API
    entityName: string = "entidad"
  ) => {
    if (isLoadingSchema) {
      return (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2">Cargando atributos para nueva {entityName}...</Typography>
          </Box>
        </Grid>
      );
    }
    if (schemaError) {
      return (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Alert severity="warning" sx={{ my: 1 }}>
            No se pudieron cargar los atributos adicionales para nueva {entityName}: {schemaError?.message || 'Error desconocido.'}
            {schemaErrorResponse && !schemaErrorResponse.success && schemaErrorResponse.message &&
              <Typography variant="caption" display="block">{schemaErrorResponse.message}</Typography>
            }
          </Alert>
        </Grid>
      );
    }
    if (attributes.length === 0 && !isLoadingSchema && !schemaError) {
      return (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Typography variant="caption" color="textSecondary">
            No hay atributos adicionales configurados para nuevas {entityName}s.
          </Typography>
        </Grid>
      );
    }
    return attributes.map((attr) => (
      <Grid item xs={12} sm={6} md={4} key={`${entityName}-${attr.name}`} sx={{ mt: 1 }}>
        {attr.type === 'select' && (
          <Autocomplete
            fullWidth
            id={`dynamic-${entityName}-attr-${attr.name}`}
            options={attr.options || []}
            value={values[attr.name] || null}
            onChange={(_event: any, newValue: string | null) => { handler(attr.name, newValue); }}
            getOptionLabel={(option) => String(option) || ""}
            isOptionEqualToValue={(option, value) => String(option) === String(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={getFieldLabel(attr.label || attr.name, attr.required || false)}
                variant="filled"
                required={attr.required || false}
                InputProps={{ // Aplicar los mismos estilos agresivos que a los Autocompletes principales
                  ...params.InputProps,
                  sx: {
                    display: 'flex',
                    width: '100%',
                    '& .MuiFilledInput-input': {
                      minWidth: '0 !important',
                      width: '100% !important',
                      textOverflow: 'clip !important',
                      overflow: 'visible !important',
                      whiteSpace: 'nowrap !important',
                    },
                  }
                }}
              />
            )}
            disabled={loadingInitialOptions || optimizationMutation.isPending}
          />
        )}
        {(attr.type === 'text' || attr.type === 'number') && (
            <TextField
                fullWidth
                id={`dynamic-${entityName}-attr-${attr.name}`}
                label={getFieldLabel(attr.label || attr.name, attr.required || false)}
                type={attr.type}
                value={values[attr.name] || ''}
                onChange={(e) => handler(attr.name, e.target.value)}
                variant="filled"
                required={attr.required || false}
                // helperText={attr.required ? "Requerido" : ""} // Opcional
                disabled={loadingInitialOptions || optimizationMutation.isPending}
            />
        )}
        {/* Aquí podrías añadir lógica para otros attr.type si los hubiera */}
      </Grid>
    ));
  };

  // --- GESTIÓN DE RENDERIZADO POR ESTADO OMEGA ---

  // CASO 0: CARGA DE SEGURIDAD
  if (systemState === 'LOADING') {
    return <SystemBootLoader isOpen={true} message="Sincronizando modelos de optimización..." />;
  }

  // CASO 1: SIN DATOS (Manda a cargar)
  if (systemState === 'NO_DATA') {
    return (
        <Box sx={{ minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {backButton}
            <EmptyStateHero 
                title="Aún no hay datos para evaluar precios"
                description="Para recomendar precios, PROA necesita tu histórico de ventas y tus costes."
            />
            {/* PRC-028: salida propia hacia la carga, con origen. */}
            <Button
                variant="contained"
                onClick={() => navigate(withReturnTo('/data-upload', currentOrigin(window.location)))}
                sx={{ mt: 3, bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 'bold', textTransform: 'none' }}
            >
                Cargar datos
            </Button>
        </Box>
    );
  }

  // CASO 2: DATOS LISTOS PERO MOTOR APAGADO (Manda a entrenar)
  if (systemState === 'NEEDS_TRAINING') {
    return (
        <Container maxWidth="md" sx={{ py: 10 }}>
            {backButton}
            <Paper 
                elevation={0}
                sx={{ 
                    p: { xs: 4, md: 6 }, textAlign: 'center', bgcolor: '#1e293b', border: '1px solid #334155', borderRadius: 2, 
                    position: 'relative', overflow: 'hidden',
                    background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.4) 100%)'
                }}
            >
                {/* Indicador de Pulso Amarillo (Consistente) */}
                <Box sx={{ position: 'absolute', top: 20, right: 30, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fcd34d', boxShadow: '0 0 8px #fcd34d' }} />
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, letterSpacing: 1 }}>
                        Esperando al motor
                    </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: 2, bgcolor: 'rgba(56, 189, 248, 0.05)', border: '1px solid rgba(56, 189, 248, 0.1)' }}>
                        <EngineeringIcon sx={{ fontSize: 32, color: '#38bdf8' }} />
                    </Box>
                </Box>
                
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#f8fafc', mb: 1.5 }}>
                    Infraestructura de Datos Lista
                </Typography>

                <Typography variant="body2" sx={{ color: '#94a3b8', mb: 5, maxWidth: '500px', mx: 'auto', lineHeight: 1.7, fontSize: '0.95rem' }}>
                    Tus registros han sido validados con éxito. Para activar el motor de precios y el análisis de elasticidad, el sistema requiere ejecutar el <Typography component="span" sx={{ color: '#38bdf8', fontWeight: 600 }}>primer ciclo de entrenamiento neuronal</Typography>.
                </Typography>

                <Stack spacing={2} alignItems="center">
                    <Button 
                        variant="contained" size="medium" startIcon={<RocketLaunchIcon />}
                        // PRC-027: la salida a Modelos conserva el origen para volver aquí.
                        onClick={() => navigate(withReturnTo('/retraining', currentOrigin(window.location)))}
                        sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800, px: 4, py: 1.2, borderRadius: 1, textTransform: 'none' }}
                    >
                        Iniciar Entrenamiento del Motor
                    </Button>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.5 }}>
                        <TimerIcon sx={{ fontSize: 14, color: '#64748b' }} />
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                            Tiempo estimado: 1 - 2 horas.
                        </Typography>
                    </Stack>
                </Stack>
            </Paper>
        </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}> {/* mt:4 y mb:4 son opcionales, para espaciado superior/inferior */}
        {/* PRC-032/033: cabecera común. */}
        <PageHeader
            eyebrow="Optimización"
            title="Evaluación rápida"
            description="Elige cliente, producto y estrategia, y calcula el precio que recomienda PROA."
            back={{ label: back.label, onClick: back.go }}
            actions={(
                <Button
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => navigate(withReturnTo('/optimization/history', currentOrigin(window.location)))}
                    sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}
                >
                    Ver historial
                </Button>
            )}
        />

        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4, backgroundColor: 'background.paper', width: '100%' }}>       
          <Typography variant="h6" component="h2" gutterBottom>
            Evalúa un precio
          </Typography>
          {/* WIDGET DE LÍMITE DE TIERS */}
          <OptimizationLimitWidget onLimitReached={setIsLimitReached} />

          {isOptimizationCachePreparing && (
            <Alert
              severity="info"
              icon={<CircularProgress size={20} />}
              sx={{ mb: 2 }}
            >
              <AlertTitle>Preparando motor de precios</AlertTitle>
              Estamos cargando de forma segura los modelos necesarios.
              El formulario puede completarse, pero la optimización se
              habilitará únicamente cuando el motor esté listo.
            </Alert>
          )}

          {(isOptimizationCacheFailed ||
            cacheReadinessTimedOut) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>
                El motor de precios no está disponible
              </AlertTitle>
              {cacheReadinessTimedOut
                ? 'La preparación no finalizó dentro de los tres minutos permitidos. No se ha enviado ninguna optimización ni se ha consumido cuota.'
                : optimizationCacheFailureMessage ||
                  'La preparación del motor terminó con un error. No se ha enviado ninguna optimización.'}
            </Alert>
          )}

          {initialOptionsError && !loadingInitialOptions && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                  {initialOptionsError}
                  {isClientError && <Typography variant="caption" display="block">Error clientes: {clientError?.message}</Typography>}
                  {isProductError && <Typography variant="caption" display="block">Error productos: {productError?.message}</Typography>}
              </Alert>
          )}
          <Box component="form" onSubmit={handleOptimize} noValidate>
            <Stack spacing={3}> {/* <--- Usando Stack para organizar secciones */}

              {/* SECCIÓN CLIENTE (Refactorizada con Flexbox) */}
              <Box sx={glassCardSx}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                          <PersonIcon sx={{ color: '#94a3b8' }} />
                          <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                              1. Cliente
                          </Typography>
                      </Stack>
                      <Button
                          size="small"
                          variant={isNewClientUI ? "contained" : "outlined"}
                          onClick={() => setIsNewClientUI(prev => !prev)}
                          startIcon={isNewClientUI ? <CheckCircleIcon /> : <AddCircleOutlineIcon />}
                          disabled={loadingInitialOptions || optimizationMutation.isPending}
                          sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              borderColor: isNewClientUI ? 'transparent' : '#334155',
                              bgcolor: isNewClientUI ? '#38bdf8' : 'transparent',
                              color: isNewClientUI ? '#0f172a' : '#94a3b8',
                              '&:hover': {
                                  borderColor: '#38bdf8',
                                  bgcolor: isNewClientUI ? '#0ea5e9' : 'rgba(56, 189, 248, 0.1)',
                                  color: isNewClientUI ? '#0f172a' : '#38bdf8'
                              }
                          }}
                      >
                          {isNewClientUI ? 'Modo Nuevo Cliente' : 'Nuevo Cliente'}
                      </Button>
                  </Stack>

                  <Autocomplete
                      id="client_id_autocomplete"
                      fullWidth
                      options={clientOptions}
                      value={clienteId}
                      onChange={(_event: any, newValue: string | null) => { setClienteId(newValue); }}
                      getOptionLabel={(option) => String(option)}
                      isOptionEqualToValue={(option, value) => String(option) === String(value)}
                      loading={loadingClients}
                      disabled={loadingInitialOptions || optimizationMutation.isPending || isNewClientUI}
                      renderInput={(params) => (
                          <TextField
                              {...params}
                              label={getFieldLabel("Cliente", !isNewClientUI)} // CAMBIO: Texto corto
                              variant="filled"
                              error={isClientError || (formAttempted && !isNewClientUI && !clienteId)}
                              sx={stealthInputSx}
                              InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                      <>
                                          {loadingClients ? <CircularProgress color="inherit" size={20} /> : null}
                                          {params.InputProps.endAdornment}
                                      </>
                                  )
                              }}
                          />
                      )}
                  />

                  {/* Campos Dinámicos Cliente */}
                  {isNewClientUI && (
                      <Box sx={{ mt: 3, p: 2, border: '1px dashed #334155', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.2)' }}>
                          <Typography variant="caption" sx={{ color: '#fcd34d', mb: 2, display: 'block', fontWeight: 700, letterSpacing: 1 }}>
                              Datos del cliente nuevo
                          </Typography>
                          <Grid container spacing={2}>
                              {renderDynamicAttributeFields(
                                  currentClientAttrFeatures,
                                  dynamicClientAttrs,
                                  handleDynamicClientAttrChange,
                                  loadingClientAttrSchema,
                                  clientAttrSchemaError,
                                  clientAttrSchemaResponse,
                                  "cliente"
                              )}
                          </Grid>
                      </Box>
                  )}
              </Box>

              {/* === SECCIÓN PRODUCTO (DARK PREMIUM REFACTOR) === */}
              <Box sx={glassCardSx}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                          <InventoryIcon sx={{ color: '#94a3b8' }} />
                          <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                              2. Producto
                          </Typography>
                      </Stack>
                      <Button
                          size="small"
                          variant={isNewProductUI ? "contained" : "outlined"}
                          onClick={() => setIsNewProductUI(prev => !prev)}
                          startIcon={isNewProductUI ? <CheckCircleIcon /> : <AddCircleOutlineIcon />}
                          disabled={loadingInitialOptions || optimizationMutation.isPending}
                          sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              borderColor: isNewProductUI ? 'transparent' : '#334155',
                              bgcolor: isNewProductUI ? '#38bdf8' : 'transparent',
                              color: isNewProductUI ? '#0f172a' : '#94a3b8',
                              '&:hover': {
                                  borderColor: '#38bdf8',
                                  bgcolor: isNewProductUI ? '#0ea5e9' : 'rgba(56, 189, 248, 0.1)',
                                  color: isNewProductUI ? '#0f172a' : '#38bdf8'
                              }
                          }}
                      >
                          {isNewProductUI ? 'Modo Nuevo Producto' : 'Nuevo Producto'}
                      </Button>
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      {/* Autocomplete Producto */}
                      <Box sx={{ flexGrow: 1 }}>
                          <Autocomplete
                              id="producto_id_autocomplete"
                              fullWidth
                              options={productOptions}
                              value={productoId}
                              onChange={(_event: any, newValue: string | null) => { setProductoId(newValue); }}
                              getOptionLabel={(option) => String(option)}
                              isOptionEqualToValue={(option, value) => String(option) === String(value)}
                              loading={loadingProducts}
                              disabled={loadingInitialOptions || optimizationMutation.isPending || isNewProductUI}
                              renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={getFieldLabel("Producto", !isNewProductUI)} // CAMBIO: Texto corto
                                    variant="filled"
                                    error={isProductError}
                                    sx={stealthInputSx}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                          />
                      </Box>

                      {/* Input Coste */}
                      <Box sx={{ minWidth: { md: '200px' } }}>
                          <TextField
                              fullWidth
                              id="coste_actual"
                              label={getFieldLabel(
                                  isNewProductUI ? "Coste Standard (€)" : "Coste (Opcional)",
                                  isNewProductUI
                              )}
                              name="coste_actual"
                              type="number"
                              inputProps={{ step: "0.01", min: "0.01" }}
                              value={costeActual}
                              onChange={(e) => setCosteActual(e.target.value)}
                              disabled={loadingInitialOptions || optimizationMutation.isPending}
                              required={isNewProductUI}
                              variant="filled"
                              error={isNewProductUI && (costeActual === '' || isNaN(parseFloat(costeActual)) || parseFloat(costeActual) <= 0)}
                              helperText={
                                  isNewProductUI && (costeActual === '' || isNaN(parseFloat(costeActual)) || parseFloat(costeActual) <= 0)
                                      ? "Requerido"
                                      : ""
                              }
                              sx={stealthInputSx} // <--- ESTILO DARK PREMIUM
                          />
                      </Box>
                      {/* Input Cantidad (Opcional) */}
                      <Box sx={{ minWidth: { md: '150px' } }}>
                          <TextField
                              fullWidth
                              id="cantidad_input"
                              label="Cantidad (Opcional)"
                              type="number"
                              value={cantidadInput}
                              onChange={(e) => setCantidadInput(e.target.value)}
                              disabled={loadingInitialOptions || optimizationMutation.isPending}
                              variant="filled"
                              sx={stealthInputSx}
                              InputProps={{ inputProps: { min: 1 } }}
                          />
                      </Box>
                  </Stack>

                  {/* Campos Dinámicos Producto */}
                  {isNewProductUI && (
                      <Box sx={{ mt: 3, p: 2, border: '1px dashed #334155', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.2)' }}>
                           <Typography variant="caption" sx={{ color: '#fcd34d', mb: 2, display: 'block', fontWeight: 700, letterSpacing: 1 }}>
                              Datos del producto nuevo
                          </Typography>
                          <Grid container spacing={2}>
                              {renderDynamicAttributeFields(
                                  currentProductAttrFeatures,
                                  dynamicProductAttrs,
                                  handleDynamicProductAttrChange,
                                  loadingProductAttrSchema,
                                  productAttrSchemaError,
                                  productAttrSchemaResponse,
                                  "producto"
                              )}
                          </Grid>
                      </Box>
                  )}
              </Box>
            
          {/* === SECCIÓN ESTRATEGIA (VISUAL SELECTOR) === */}
              <Box>
                  <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700, mb: 2 }}>
                      3. Estrategia
                  </Typography>
                  <Grid container spacing={2}>
                      {[
                          { id: 'conservador', label: 'Conservadora', icon: <SecurityIcon />, color: '#38bdf8', desc: 'Prioriza conservar el volumen y el cliente.' },
                          { id: 'equilibrado', label: 'Equilibrada', icon: <BalanceIcon />, color: '#38bdf8', desc: 'Equilibra margen y riesgo de perder ventas.' },
                          { id: 'agresivo', label: 'Agresiva', icon: <RocketLaunchIcon />, color: '#38bdf8', desc: 'Busca el máximo margen por unidad.' },
                      ].map((strategy) => (
                          <Grid item xs={12} md={4} key={strategy.id}>
                              <Paper
                                  onClick={(e) => handleStrategyChange(e, strategy.id)}
                                  sx={{
                                      p: 2,
                                      cursor: 'pointer',
                                      bgcolor: selectedStrategy === strategy.id ? alpha(strategy.color, 0.1) : 'rgba(255,255,255,0.02)',
                                      border: `1px solid ${selectedStrategy === strategy.id ? strategy.color : '#334155'}`,
                                      borderRadius: 2,
                                      transition: 'all 0.2s',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      textAlign: 'center',
                                      opacity: isOptimizeButtonDisabled ? 0.5 : 1,
                                      '&:hover': {
                                          borderColor: strategy.color,
                                          transform: 'translateY(-2px)',
                                          boxShadow: `0 4px 20px -5px ${alpha(strategy.color, 0.3)}`
                                      }
                                  }}
                              >
                                  <Box sx={{ color: selectedStrategy === strategy.id ? strategy.color : '#94a3b8', mb: 1 }}>{strategy.icon}</Box>
                                  <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                                      {strategy.label}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                      {strategy.desc}
                                  </Typography>
                              </Paper>
                          </Grid>
                      ))}
                  </Grid>

                  {/* --- INICIO INSERCIÓN: SLIDER V2.0 --- */}
                  <Box sx={{ px: 2, pt: 4, pb: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                              Cuánto seguir la recomendación del modelo
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#38bdf8', fontWeight: 700 }}>
                              {strategySensitivity} %
                              {strategySensitivity === 0 && ' · tus precios actuales'}
                              {strategySensitivity === 100 && ' · todo el potencial'}
                          </Typography>
                      </Stack>
                      {/* PRC-033: «Sensibilidad a la IA» explicada en lenguaje de negocio. */}
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
                          Con 0 % el precio se queda como lo pondrías hoy; con 100 % se aplica toda la mejora de margen que ve el modelo para esta venta.
                      </Typography>
                      <Slider
                          value={strategySensitivity}
                          onChange={handleSliderChange}
                          step={1}
                          min={0}
                          max={100}
                          sx={{
                              color: '#38bdf8',
                              height: 6,
                              '& .MuiSlider-track': { border: 'none' },
                              '& .MuiSlider-thumb': {
                                  height: 20, width: 20, backgroundColor: '#1e293b', border: '2px solid currentColor',
                                  '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': { boxShadow: 'inherit' },
                                  '&:before': { display: 'none' },
                              },
                              '& .MuiSlider-mark': { backgroundColor: '#bfbfbf', height: 8, width: 1, '&.MuiSlider-markActive': { opacity: 1, backgroundColor: 'currentColor' } },
                          }}
                      />
                  </Box>
                  {/* --- FIN INSERCIÓN --- */}

              </Box>

              {/* === BOTÓN DE EJECUCIÓN === */}
              <Box sx={{ pt: 2 }}>
                  <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={isOptimizeButtonDisabled}
                      startIcon={uiState.name === 'optimizing' ? <CircularProgress size={24} color="inherit" /> : <AutoAwesomeIcon />}
                      sx={{
                          py: 1.5, // Altura reducida ligeramente
                          fontSize: '1rem',
                          fontWeight: 600, // Menos negrita
                          borderRadius: 1,
                          textTransform: 'none', // Texto normal, no mayúsculas forzadas
                          background: isOptimizeButtonDisabled 
                            ? '#334155' 
                            : '#0ea5e9', // Azul sólido profesional
                          boxShadow: 'none', // Sin brillos
                          '&:hover': {
                              background: '#0284c7',
                              boxShadow: 'none'
                          }
                      }}
                  >
                      {uiState.name === 'optimizing' ? 'Calculando…' : 'Calcular precio recomendado'}
                  </Button>
              </Box>

            </Stack>
          </Box>
        </Paper>

      {/* ================================================================== */}
      {/* === VISUALIZACIÓN DE ESTADOS (LOADING / ERROR / SUCCESS)       === */}
      {/* ================================================================== */}

      {uiState.name === 'optimizing' && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={60} thickness={4} sx={{ color: '#38bdf8', mb: 3 }} />
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 300 }}>
                Calculando el precio recomendado…
            </Typography>
        </Box>
      )}

      {uiState.name === 'error' && (
        <Alert 
            severity="error" 
            variant="filled" 
            sx={{ 
                my: 3, 
                bgcolor: 'rgba(239, 68, 68, 0.1)', 
                border: '1px solid #ef4444', 
                color: '#fca5a5' 
            }}
        >
          <AlertTitle sx={{ fontWeight: 800 }}>No se ha podido calcular el precio</AlertTitle>
          {getOptimizeErrorMessage(uiState.error)}
        </Alert>
      )}

      {uiState.name === 'success' && finalData && (
        <Box sx={{ animation: 'fadeIn 0.5s ease-out' }}>
            {/* PRC-033: misma tarjeta de recomendación que el historial y la evaluación guardada. */}
            <Paper sx={{ ...glassCardSx, mb: 4, borderColor: '#286779' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px,1fr) minmax(160px,0.7fr) minmax(260px,1fr)' }, gap: 2.5, alignItems: 'center' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: '#a7c2d3', fontWeight: 800 }}>Precio que recomienda PROA</Typography>
                        <Typography variant="h4" sx={{ color: '#a7f5e5', fontWeight: 900, letterSpacing: '-0.04em', fontVariantNumeric: 'tabular-nums' }}>
                            {finalData.precio_sugerido != null ? `${finalData.precio_sugerido.toFixed(2)} €` : '—'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#a7c2d3' }}>
                            Para {cantidadInput && parseFloat(cantidadInput) > 0 ? `${cantidadInput} unidades` : '1 unidad'}
                        </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: '#93abc2', fontWeight: 700 }}>Margen sobre ingresos</Typography>
                        <Typography variant="h6" sx={{ color: '#edf6ff', fontWeight: 820, fontVariantNumeric: 'tabular-nums' }}>
                            {finalData.margen_sugerido != null ? `${finalData.margen_sugerido.toFixed(1)} %` : '—'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 1.3 }}>
                        {finalData.list_price_used && finalData.list_price_used > 0 ? (
                            <Box>
                                <Typography variant="caption" sx={{ color: '#93abc2', fontWeight: 700 }}>Precio de tarifa</Typography>
                                <Typography sx={{ color: '#edf6ff', fontWeight: 820, fontVariantNumeric: 'tabular-nums' }}>{finalData.list_price_used.toFixed(2)} €</Typography>
                                {finalData.precio_sugerido && finalData.list_price_used > finalData.precio_sugerido && (
                                    <Typography variant="caption" sx={{ color: '#829ab0' }}>
                                        Descuento del {((1 - (finalData.precio_sugerido / finalData.list_price_used)) * 100).toFixed(1)} %
                                    </Typography>
                                )}
                            </Box>
                        ) : <Box />}
                        {finalData.pricing_matrix && finalData.pricing_matrix.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    size="small"
                                    onClick={() => document.getElementById('pricing-matrix-section')?.scrollIntoView({ behavior: 'smooth' })}
                                    sx={{ color: '#7dd3fc', textTransform: 'none', fontWeight: 700, px: 0.5 }}
                                >
                                    Ver precios por volumen ↓
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>

                 {/* Value Bridge Component */}
                 {finalData.optimization_level_applied === 'MP' && finalData.base_historical_solid_cp != null && (
                    <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #334155' }}>
                        <ValueBridge
                            base_historical_solid_cp={finalData.base_historical_solid_cp}
                            calculated_suggested_margin={finalData.margen_sugerido ? finalData.margen_sugerido / 100 : 0}
                            mei_markup_mp_plus_mde={finalData.mei_markup_mp_plus_mde || 0}
                            applied_strategy_profile={finalData.applied_strategy_profile || 'equilibrado'}
                            tacticalIntelligence={finalData.tactical_intelligence}
                        />
                    </Box>
                )}
            </Paper>

            {/* 1.5. TABLA DE MATRIZ DE PRECIOS (NUEVO) */}
            {finalData.pricing_matrix && finalData.pricing_matrix.length > 0 && (
                <Paper 
                    id="pricing-matrix-section" // <--- AÑADIR ESTE ID AQUÍ
                    sx={{ ...glassCardSx, mb: 4, mt: 2, overflow: 'hidden' }}
                >
                    <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700, mb: 2 }}>
                        Precios por volumen
                    </Typography>
                    
                    <TableContainer>
                        <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: '#1e293b', color: '#cbd5e1' }, '& .MuiTableCell-head': { color: '#94a3b8', fontWeight: 700, fontSize: 12.5 } }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#cbd5e1' }}>
                                                Cantidad
                                            </Typography>
                                            <Tooltip 
                                                title="Estos rangos se calculan automáticamente basándose en el historial de transacciones (cantidades pequeñas, habituales y grandes de este producto)." 
                                                arrow 
                                                placement="top"
                                            >
                                                <InfoOutlinedIcon sx={{ fontSize: 16, color: '#64748b', cursor: 'help' }} />
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">Precio recomendado</TableCell>
                                    <TableCell align="right">Descuento</TableCell>
                                    <TableCell align="right">Margen por unidad</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {finalData.pricing_matrix.map((tier, index) => {
                                    // SEMÁNTICA DEFINIDA: Puntual / Recurrente / Gran Escala
                                    let label = "Volumen";
                                    let color = "#94a3b8"; // Gris por defecto
                                    
                                    if (index === 0) {
                                        label = "Puntual";
                                        color = "#94a3b8"; // Gris (Neutro)
                                    } else if (index === 1) {
                                        label = "Recurrente";
                                        color = "#38bdf8"; // Azul (Destacado/Habitual)
                                    } else if (index === 2) {
                                        label = "Gran Escala";
                                        color = "#4ade80"; // Verde (Oportunidad)
                                    }

                                    return (
                                        <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell component="th" scope="row">
                                                <Stack direction="column">
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: color }}>
                                                        {label}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                                                        {tier.quantity} uds
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: '#f8fafc' }}>
                                                {tier.suggested_price.toFixed(2)} €
                                            </TableCell>
                                            <TableCell align="right">
                                                {tier.suggested_discount > 0 ? (
                                                    <Chip 
                                                        label={`-${(tier.suggested_discount * 100).toFixed(1)}%`} 
                                                        size="small" 
                                                        sx={{ 
                                                            bgcolor: 'rgba(74, 222, 128, 0.1)', 
                                                            color: '#4ade80', 
                                                            fontWeight: 700, 
                                                            height: 20,
                                                            fontSize: '0.7rem'
                                                        }} 
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">-</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: '#94a3b8' }}>
                                                {tier.unit_margin.toFixed(2)} €
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {finalData.list_price_used !== undefined && finalData.list_price_used !== null && finalData.list_price_used > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'right', color: '#64748b' }}>
                            * Descuentos calculados sobre Precio Tarifa: {finalData.list_price_used.toFixed(2)} €
                        </Typography>
                    )}
                </Paper>
            )}
        </Box>
      )}
    </Container>
  );
};

export default OptimizationPage;